<template>
  <div v-if="loading || similarItems.length > 0" class="similar-items-section mt-12 pt-10 border-t border-slate-200 print:hidden">
    <div class="flex items-center gap-3 mb-8">
      <div class="w-1.5 h-8 bg-blue-600 rounded-full"></div>
      <h2 class="text-2xl font-bold text-slate-900">
        {{ title || (type === 'machines' ? $t('machineDetails.sections.similarMachines', 'Similar Machines') : $t('sparePartDetails.sections.similarParts', 'Similar Parts')) }}
      </h2>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="featured-carousel">
      <div class="featured-carousel__track">
        <div v-for="i in 4" :key="i" class="featured-card featured-card--loading">
          <div class="featured-card__image-skeleton"></div>
          <div class="featured-card__content">
            <div class="skeleton-line skeleton-line--sm"></div>
            <div class="skeleton-line skeleton-line--lg"></div>
            <div class="skeleton-line skeleton-line--md"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Items Grid (Featured Card Style) -->
    <div v-else class="featured-grid">
      <NuxtLink
        v-for="item in similarItems"
        :key="item.id"
        :to="`/${type}/${item.id}`"
        class="featured-card"
      >
        <!-- Item Image -->
        <div class="featured-card__image">
          <img
            v-if="getImageSrc(item)"
            :src="getImageSrc(item)"
            :alt="item.title || item.name"
            loading="lazy"
            @error="(e) => handleImageError(e)"
          />
          <div v-else class="featured-card__placeholder">
            <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          <!-- Overlay Gradient -->
          <div class="featured-card__overlay"></div>

          <!-- Badges -->
          <div class="featured-card__badges">
            <span v-if="item.status" class="featured-badge" :class="getStatusClass(item.status)">
              {{ getStatusLabel(item.status) }}
            </span>
            <span v-if="item.year_manufactured || item.year" class="featured-badge featured-badge--year">
              {{ item.year_manufactured || item.year }}
            </span>
          </div>

          <!-- Quick View Hover -->
          <div class="featured-card__hover">
            <span class="featured-card__hover-text">
              {{ $t('common.viewDetails') }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </span>
          </div>
        </div>

        <!-- Details -->
        <div class="featured-card__content">
          <div class="featured-card__tags">
            <span v-if="item.brand" class="featured-tag featured-tag--brand">
              {{ item.brand.name }}
            </span>
            <span v-if="item.category" class="featured-tag featured-tag--category">
              {{ item.category.name }}
            </span>
          </div>

          <h3 class="featured-card__title">{{ item.title || item.name }}</h3>

          <div class="featured-card__specs">
            <span v-if="item.condition" class="featured-spec">
              <span class="featured-spec__dot" :class="item.condition === 'new' ? 'featured-spec__dot--new' : 'featured-spec__dot--used'"></span>
              {{ item.condition }}
            </span>
            <span v-if="type === 'spare-parts' && item.stock_quantity !== undefined" class="featured-spec ml-auto text-sm font-medium" :class="item.stock_quantity > 0 ? 'text-green-600' : 'text-red-600'">
              {{ item.stock_quantity > 0 ? $t('sparePartDetails.inStock') : $t('sparePartDetails.outOfStock') }}
            </span>
          </div>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  categoryId: {
    type: [String, Number],
    required: false
  },
  excludeId: {
    type: [String, Number],
    required: true
  },
  type: {
    type: String,
    required: true,
    validator: (value) => ['machines', 'spare-parts'].includes(value)
  },
  title: {
    type: String,
    default: ''
  }
});

const { getSafeImageUrl, handleImageError } = useImageHandling();
const { t } = useI18n();

const similarItems = ref([]);
const loading = ref(true);

const getImageSrc = (item) => {
  if (!item.images || item.images.length === 0) return null;
  const image = item.images[0];
  const path = image.file_path || image.url || image.full_url;
  return getSafeImageUrl(path);
};

const getStatusClass = (status) => {
  const statusValue = typeof status === 'object' ? status.value : status;
  const statusMap = {
    'available': 'featured-badge--available',
    'published': 'featured-badge--available',
    'reserved': 'featured-badge--reserved',
    'sold': 'featured-badge--sold',
    'draft': 'featured-badge--draft'
  };
  return statusMap[statusValue] || 'featured-badge--draft';
};

const getStatusLabel = (status) => {
  const statusValue = typeof status === 'object' ? status.value : status;
  const statusKey = `machines.status.${statusValue}`;
  const translated = t(statusKey);
  if (translated === statusKey) {
    return statusValue ? statusValue.charAt(0).toUpperCase() + statusValue.slice(1) : '';
  }
  return translated;
};

const fetchSimilarItems = async () => {
  if (!props.categoryId) {
    loading.value = false;
    similarItems.value = [];
    return;
  }

  try {
    loading.value = true;
    const config = useRuntimeConfig();
    const apiBase = config.public?.apiBase || '/api/v1';

    // Build correct query string
    const queryParams = {
      exclude_id: props.excludeId,
      per_page: 4,
    };

    if (props.type === 'machines') {
      queryParams.machine_category = props.categoryId;
    } else {
      queryParams.category_id = props.categoryId;
    }

    const query = new URLSearchParams(queryParams);
    
    // Add endpoint-specific public filters if necessary
    // (Both machines and spare-parts handle active status implicitly in typical search/index)
    // Actually, Machines requires no additional things for Index. Let's just use limit.

    const endpoint = props.type === 'machines' ? '/machines' : '/spare-parts';
    const requestUrl = `${apiBase}${endpoint}?${query.toString()}`;

    const response = await $fetch(requestUrl);
    
    // Handle both wrapped `{ data: { data: [...] }}` and `{ data: [...] }` formats
    let items = [];
    if (response?.data?.data) {
      items = response.data.data;
    } else if (response?.data) {
      // It could be just response.data
      items = Array.isArray(response.data) ? response.data : [];
    } else if (Array.isArray(response)) {
      items = response;
    }
    
    similarItems.value = items;
  } catch (error) {
    console.error('Error fetching similar items:', error);
    similarItems.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchSimilarItems();
});

watch(() => props.categoryId, () => {
  fetchSimilarItems();
});
</script>

<style scoped>
/* Reusing the exact FeaturedMachinesClean styles for consistent aesthetic */
.featured-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 640px) {
  .featured-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .featured-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Base Card */
.featured-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.02);
  border: 1px solid #e2e8f0;
  width: 100%;
}

.featured-card:hover {
  transform: translateY(-4px) scale(1.01);
  box-shadow: 0 15px 35px -8px rgba(0, 0, 0, 0.15);
  border-color: #3b82f6;
}

/* Loading Skeletons */
.featured-carousel__track {
  display: flex;
  gap: 24px;
}
.featured-card--loading {
  pointer-events: none;
  flex: 1;
}
.featured-card__image-skeleton {
  height: 200px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.skeleton-line {
  height: 16px;
  background: #f1f5f9;
  border-radius: 8px;
  margin-bottom: 12px;
}
.skeleton-line--sm { width: 30%; }
.skeleton-line--md { width: 50%; }
.skeleton-line--lg { width: 80%; }

/* Image Area */
.featured-card__image {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}
.featured-card__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}
.featured-card:hover .featured-card__image img {
  transform: scale(1.03);
}
.featured-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
}
.featured-card__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.6) 100%);
  opacity: 0;
  transition: opacity 0.4s ease;
}
.featured-card:hover .featured-card__overlay {
  opacity: 1;
}

/* Badges */
.featured-card__badges {
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.featured-badge {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  backdrop-filter: blur(8px);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.featured-badge--available {
  background: rgba(34, 197, 94, 0.9);
  color: #ffffff;
}
.featured-badge--reserved {
  background: rgba(245, 158, 11, 0.9);
  color: #ffffff;
}
.featured-badge--sold {
  background: rgba(239, 68, 68, 0.9);
  color: #ffffff;
}
.featured-badge--draft {
  background: rgba(100, 116, 139, 0.9);
  color: #ffffff;
}
.featured-badge--year {
  background: rgba(255, 255, 255, 0.95);
  color: #334155;
  margin-left: auto;
}

/* Quick View Hover Indicator */
.featured-card__hover {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  opacity: 0;
  transition: all 0.4s ease;
}
.featured-card:hover .featured-card__hover {
  transform: translateX(-50%) translateY(0);
  opacity: 1;
}
.featured-card__hover-text {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #ffffff;
  color: #1e40af;
  font-size: 14px;
  font-weight: 600;
  border-radius: 25px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

/* Content Area */
.featured-card__content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
.featured-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.featured-tag {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
}
.featured-tag--brand {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
}
.featured-tag--category {
  background: #f1f5f9;
  color: #475569;
}
.featured-card__title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 16px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.3s ease;
}
.featured-card:hover .featured-card__title {
  color: #2563eb;
}

/* Specs Footer */
.featured-card__specs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}
.featured-spec {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #64748b;
  text-transform: capitalize;
}
.featured-spec__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.featured-spec__dot--new {
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.4);
}
.featured-spec__dot--used {
  background: #f59e0b;
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.4);
}
</style>
